import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Header from "components/Header/Header.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import { api } from "common";
import image from "../../assets/img/signIn-bg2.webp";
import { makeStyles } from "@mui/styles";
import styles from "../../styles/loginPage";
import { useTranslation } from "react-i18next";
import Availability from "components/Availability";
import HomeFooter from "components/Footer/HomeFooter";
import { useNavigate } from "react-router-dom";
import { firebase } from "common/src/config/configureFirebase";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: "transparent",
    "&:hover": { backgroundColor: "transparent" },
    "&.Mui-focused": { backgroundColor: "transparent" },
    "&:before": { borderBottom: "1px solid rgba(255, 255, 255, 0.7)" },
    "&:after": { borderBottom: "2px solid #fff" },
    "&:hover:not(.Mui-disabled):before": { borderBottom: "2px solid #fff" },
  },
  "& .MuiFilledInput-input": {
    padding: "8px 0",
    color: "#fff",
    fontSize: "1.1rem",
    backgroundColor: "transparent",
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px transparent inset",
      WebkitTextFillColor: "#fff",
      caretColor: "#fff",
      transition: "background-color 5000s ease-in-out 0s",
    },
  },
  "& .MuiFormLabel-root": {
    color: "#fff",
    fontSize: "1rem",
    fontWeight: 500,
    transform: "none",
    position: "relative",
    marginBottom: "8px",
  },
  "& .MuiFormLabel-root.Mui-focused": { color: "#fff" },
}));

const useStyles = makeStyles(styles);

const ForgotPassword = (props) => {
  const navigate = useNavigate();
  const settings = useSelector((state) => state.settingsdata.settings);
  const { countries } = api;
  const { config } = firebase;

  // Step state: 1 = phone, 2 = OTP, 3 = new password
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const classes = useStyles();
  const { ...rest } = props;
  const { t } = useTranslation();

  // Set default country from settings
  useEffect(() => {
    if (settings && countries) {
      const found = countries.find((c) => c.label === settings.country);
      if (found) setSelectedCountry(found);
    }
  }, [settings, countries]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const getFullMobile = () => {
    if (!selectedCountry || !phone) return "";
    const formattedPhone = phone.replace(/\s/g, "");
    return `+${selectedCountry.phone}${formattedPhone}`;
  };

  // Step 1: Request OTP
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError("");

    if (!phone) {
      setError(t("phone_required") || "Please enter your phone number");
      return;
    }

    if (!selectedCountry) {
      setError(t("country_required") || "Please select your country");
      return;
    }

    setIsLoading(true);

    try {
      const fullMobile = getFullMobile();
      let host =
        window &&
        window.location &&
        settings.CompanyWebsite === window.location.origin
          ? window.location.origin
          : `https://${config.projectId}.web.app`;

      const response = await fetch(`${host}/request_password_reset_otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile: fullMobile, method: "sms" }),
      });

      const result = await response.json();

      if (result.success) {
        setStep(2);
        setCountdown(60);
      } else {
        setError(result.error || "Failed to send OTP");
      }
    } catch (err) {
      setError(err.message || "Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Step 2: Verify OTP and go to password step
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");

    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      setError("Please enter the complete 6-digit OTP");
      return;
    }

    setStep(3);
  };

  // Step 3: Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    // Validate password
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      setError(
        "Password must be at least 8 characters with uppercase, lowercase, number, and special character"
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      const fullMobile = getFullMobile();
      const otpValue = otp.join("");

      let host =
        window &&
        window.location &&
        settings.CompanyWebsite === window.location.origin
          ? window.location.origin
          : `https://${config.projectId}.web.app`;

      const response = await fetch(`${host}/verify_password_reset_otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobile: fullMobile,
          otp: otpValue,
          newPassword: newPassword,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(result.error || "Failed to reset password");
        // If OTP error, go back to OTP step
        if (result.error?.includes("OTP")) {
          setStep(2);
        }
      }
    } catch (err) {
      setError(err.message || "Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (countdown > 0) return;
    setError("");
    setIsLoading(true);

    try {
      const fullMobile = getFullMobile();
      let host =
        window &&
        window.location &&
        settings.CompanyWebsite === window.location.origin
          ? window.location.origin
          : `https://${config.projectId}.web.app`;

      const response = await fetch(`${host}/request_password_reset_otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile: fullMobile, method: "sms" }),
      });

      const result = await response.json();

      if (result.success) {
        setCountdown(60);
        setOtp(["", "", "", "", "", ""]);
      } else {
        setError(result.error || "Failed to resend OTP");
      }
    } catch (err) {
      setError(err.message || "Network error");
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <Stack spacing={3}>
      <Box>
        <Typography sx={{ color: "#fff", mb: 1 }}>
          {t("phone") || "Phone Number"}
        </Typography>
        <StyledTextField
          fullWidth
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="8012345678"
          variant="filled"
          autoComplete="tel"
          error={Boolean(error)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ height: "100%", maxHeight: "none", alignItems: "center" }}>
                <Select
                  value={selectedCountry?.phone || ""}
                  onChange={(e) => {
                    const country = countries.find(c => c.phone === e.target.value);
                    setSelectedCountry(country);
                  }}
                  variant="standard"
                  disableUnderline
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                    minWidth: 70,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    "& .MuiSelect-select": {
                      padding: "0 24px 0 0",
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiSelect-icon": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                    "&:before, &:after": {
                      display: "none",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        bgcolor: "#333",
                        "& .MuiMenuItem-root": {
                          color: "#fff",
                        },
                      },
                    },
                  }}
                >
                  {countries && countries.map((country) => (
                    <MenuItem key={country.code} value={country.phone}>
                      +{country.phone}
                    </MenuItem>
                  ))}
                </Select>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isLoading}
        sx={{
          bgcolor: "#5BAA59",
          color: "#fff",
          py: 1.5,
          borderRadius: "50px",
          fontSize: "1.1rem",
          textTransform: "none",
          "&:hover": { bgcolor: "#6BB566" },
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : t("send_otp") || "Send OTP"}
      </Button>
    </Stack>
  );

  const renderStep2 = () => (
    <Stack spacing={3}>
      <Typography sx={{ color: "#fff", textAlign: "center" }}>
        {t("enter_otp") || "Enter the 6-digit OTP sent to your phone"}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
        {otp.map((digit, index) => (
          <TextField
            key={index}
            id={`otp-${index}`}
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleOtpKeyDown(index, e)}
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: "center",
                fontSize: "1.5rem",
                color: "#fff",
                padding: "12px",
              },
            }}
            sx={{
              width: 50,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "rgba(255, 255, 255, 0.7)" },
                "&:hover fieldset": { borderColor: "#fff" },
                "&.Mui-focused fieldset": { borderColor: "#5BAA59" },
              },
            }}
          />
        ))}
      </Box>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={otp.join("").length !== 6}
        sx={{
          bgcolor: "#5BAA59",
          color: "#fff",
          py: 1.5,
          borderRadius: "50px",
          fontSize: "1.1rem",
          textTransform: "none",
          "&:hover": { bgcolor: "#6BB566" },
        }}
      >
        {t("verify_otp") || "Verify OTP"}
      </Button>
      <Button
        onClick={handleResendOtp}
        disabled={countdown > 0 || isLoading}
        sx={{
          color: countdown > 0 ? "rgba(255, 255, 255, 0.5)" : "#fff",
          textTransform: "none",
        }}
      >
        {countdown > 0
          ? `${t("resend_otp") || "Resend OTP"} (${countdown}s)`
          : t("resend_otp") || "Resend OTP"}
      </Button>
    </Stack>
  );

  const renderStep3 = () => (
    <Stack spacing={3}>
      <Box>
        <Typography sx={{ color: "#fff", mb: 1 }}>
          {t("new_password") || "New Password"}
        </Typography>
        <StyledTextField
          fullWidth
          type={showPassword ? "text" : "password"}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          variant="filled"
          error={Boolean(error)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box>
        <Typography sx={{ color: "#fff", mb: 1 }}>
          {t("confirm_password") || "Confirm Password"}
        </Typography>
        <StyledTextField
          fullWidth
          type={showConfirmPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          variant="filled"
          error={Boolean(error)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Typography sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "0.8rem" }}>
        Password must be at least 8 characters with uppercase, lowercase, number, and special character
      </Typography>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isLoading || !newPassword || !confirmPassword}
        sx={{
          bgcolor: "#5BAA59",
          color: "#fff",
          py: 1.5,
          borderRadius: "50px",
          fontSize: "1.1rem",
          textTransform: "none",
          "&:hover": { bgcolor: "#6BB566" },
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : t("reset_password") || "Reset Password"}
      </Button>
    </Stack>
  );

  const handleSubmit = (e) => {
    if (step === 1) handleRequestOtp(e);
    else if (step === 2) handleVerifyOtp(e);
    else if (step === 3) handleResetPassword(e);
  };

  const getTitle = () => {
    if (step === 1) return t("forgotten") || "Forgot Password";
    if (step === 2) return t("verify_otp") || "Verify OTP";
    return t("reset_password") || "Reset Password";
  };

  const getSubtitle = () => {
    if (step === 1) return t("nothingToWorry") || "Enter your phone number to receive a verification code";
    if (step === 2) return `OTP sent to ${getFullMobile()}`;
    return "Create a new password for your account";
  };

  return (
    <div>
      <div
        style={{
          backgroundImage: "url(" + image + ")",
          backgroundSize: "cover",
          backgroundPosition: "top center",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Header
          absolute
          color="transparent"
          rightLinks={<HeaderLinks />}
          {...rest}
        />
        <div className={classes.pageHeader}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: "100%",
              maxWidth: 500,
              marginTop: "15vh",
              zIndex: 2,
              p: 4,
              borderRadius: 4,
            }}
          >
            <Typography
              variant="h4"
              align="center"
              sx={{ color: "#fff", mb: 2, fontWeight: 600 }}
            >
              {getTitle()}
            </Typography>

            <Typography
              align="center"
              sx={{ color: "rgb(255, 255, 255)", mb: 4 }}
            >
              {getSubtitle()}
            </Typography>

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2, bgcolor: "rgba(211, 47, 47, 0.1)" }}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                severity="success"
                sx={{ mb: 2, bgcolor: "rgb(98, 255, 106)" }}
              >
                Password reset successful! Redirecting to login...
              </Alert>
            )}

            <div style={{ maxWidth: "400px", margin: "auto" }}>
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
            </div>

            {step > 1 && !success && (
              <Button
                onClick={() => setStep(step - 1)}
                sx={{
                  color: "#fff",
                  textTransform: "none",
                  mt: 2,
                  display: "block",
                  mx: "auto",
                }}
              >
                {t("back") || "Back"}
              </Button>
            )}
          </Box>
        </div>
      </div>

      <Availability />
      <HomeFooter />
    </div>
  );
};

export default ForgotPassword;
